import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, PenLine } from "lucide-react";
import { api, srcSet, sized } from "../lib/api";
import { BlurImage } from "./BlurImage";

// Single source of truth: all image URL normalization + resizing lives in lib/api.js (sized/srcSet).
// thumb() is a thin alias kept for existing callers so desktop & mobile share one implementation.
export const thumb = (u, w) => sized(u, w);
const LIMIT = 24;

// Reusable masonry grid with infinite scroll + lightbox. `filters` = {location} or {collection}.
export function PhotoGrid({ filters = {}, emptyText = "No photographs yet.", excludeId }) {
  const [photos, setPhotos] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const sentinel = useRef(null);
  const loadingRef = useRef(false);
  const key = JSON.stringify(filters);
  const keyRef = useRef(key);
  keyRef.current = key;

  // Close the lightbox on Escape, and lock background scroll while it's open.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightbox]);

  const fetchPage = useCallback(async (reset, force) => {
    if (loadingRef.current && !force) return;
    loadingRef.current = true;
    setLoading(true);
    const off = reset ? 0 : offset;
    const reqKey = keyRef.current;
    try {
      const r = await api.get("/photos", { params: { limit: LIMIT, offset: off, ...filters } });
      if (keyRef.current !== reqKey) return; // filters changed mid-flight — discard stale page
      setPhotos((prev) => {
        const base = reset ? [] : prev;
        const seen = new Set(base.map((p) => p.id));
        return [...base, ...r.data.items.filter((p) => !seen.has(p.id))];
      });
      setOffset(off + r.data.items.length);
      setHasMore(r.data.has_more);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [offset, filters]);

  useEffect(() => {
    setPhotos([]); setOffset(0); setHasMore(true);
    fetchPage(true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => {
      if (e[0].isIntersecting && hasMore && !loadingRef.current) fetchPage(false);
    }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, fetchPage]);

  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
        {photos.filter((p) => !excludeId || p.id !== excludeId).map((p) => (
          <motion.figure key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mb-6 break-inside-avoid cursor-zoom-in" data-testid={`photo-${p.id}`} onClick={() => setLightbox(p)}>
            <BlurImage src={thumb(p.image, 800)} srcSet={srcSet(p.image)} sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw" tiny={thumb(p.image, 32)} alt={p.alt || p.title} width={p.width || undefined} height={p.height || undefined}
              className="w-full object-cover border border-[#E4E1DB] transition-opacity hover:opacity-90" />
            <figcaption className="pt-3">
              {p.location && <p className="overline">{p.location}</p>}
              {p.title && <p className="font-display text-lg">{p.title}</p>}
            </figcaption>
          </motion.figure>
        ))}
      </div>

      <div ref={sentinel} className="h-10" />
      {loading && <p className="text-center text-sm text-[#9a9793] py-6">Loading…</p>}
      {!loading && photos.length === 0 && <p className="text-[#4A4947]">{emptyText}</p>}
      {!hasMore && photos.length > 0 && <p className="text-center text-xs text-[#9a9793] py-6">— end —</p>}

      {createPortal(
        <AnimatePresence>
          {lightbox && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] bg-[#1C1B1A]/95 flex items-center justify-center p-4 sm:p-12"
              onClick={() => setLightbox(null)} data-testid="photo-lightbox">
              <button className="fixed top-5 right-5 z-[10000] text-white/90 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-3 transition-colors" onClick={(e) => { e.stopPropagation(); setLightbox(null); }} data-testid="lightbox-close" aria-label="Close photo"><X className="w-6 h-6" /></button>
              <motion.div initial={{ scale: 0.96 }} animate={{ scale: 1 }} exit={{ scale: 0.96 }}
                className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                <img src={thumb(lightbox.image, 1600)} alt={lightbox.alt || lightbox.title} className="max-h-[74vh] max-w-[92vw] w-auto object-contain" />
                <div className="text-center mt-5 text-[#F0EFEA]">
                  {lightbox.location && <p className="overline text-[#C8A99A]">{lightbox.location}</p>}
                  {lightbox.title && <p className="font-display text-2xl mt-1">{lightbox.title}</p>}
                  {lightbox.caption && <p className="text-sm text-[#C7C4BF] mt-2 max-w-xl mx-auto leading-relaxed">{lightbox.caption}</p>}
                  {lightbox.collections?.length > 0 && <p className="text-xs text-[#9a9793] mt-3">{lightbox.collections.join(" · ")}</p>}
                  <Link
                    to={`/contact?topic=photo-commission&prefill=${encodeURIComponent(`I'd love to commission an etching of your photograph "${lightbox.title || "this image"}"${lightbox.location ? ` (${lightbox.location})` : ""}. Could you tell me more about making this into an etching?`)}`}
                    data-testid="turn-into-etching"
                    className="inline-flex items-center gap-2 mt-6 text-sm text-[#F0EFEA] border border-[#C8A99A]/50 hover:border-[#C8A99A] hover:text-white px-5 py-2.5 transition-colors">
                    <PenLine className="w-4 h-4" /> Turn this into an etching
                  </Link>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
